import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

const DAYS = ["Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado", "Domingo"];
const SHORT = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];

const DEFAULT_MESSAGE =
  "⏰ No momento estamos *fora do horário de atendimento humano*.\n\n" +
  "Nosso horário de atendimento:\n{horarios}\n\n" +
  "Retorne dentro desse horário que um atendente vai te ajudar. " +
  "Enquanto isso, digite *MENU* para 2ª via, PIX e outros serviços automáticos. 😉";

export function defaultHumanHours() {
  const days = {};
  for (let d = 0; d < 7; d++) {
    days[d] = d <= 4 ? { enabled: true, start: "08:00", end: "18:00" }
      : d === 5 ? { enabled: true, start: "08:00", end: "12:00" }
      : { enabled: false, start: "08:00", end: "12:00" };
  }
  return { enabled: false, days, message: DEFAULT_MESSAGE };
}

// Mesmo agrupamento do backend (business_hours.format_schedule) - so pra previa.
function scheduleText(cfg) {
  const label = (d) => {
    const x = cfg.days[d];
    return x?.enabled ? `${x.start} às ${x.end}` : "fechado";
  };
  const lines = [];
  for (let d = 0; d < 7;) {
    let e = d;
    while (e + 1 < 7 && label(e + 1) === label(d)) e++;
    lines.push(`• ${d === e ? SHORT[d] : `${SHORT[d]} a ${SHORT[e]}`}: ${label(d)}`);
    d = e + 1;
  }
  return lines.join("\n");
}

// Horario de atendimento humano: fora dele, quem pede atendente recebe um
// aviso com os horarios (e a conversa nao entra na fila).
export default function HumanHoursEditor({ value, onChange }) {
  const cfg = value || defaultHumanHours();
  const set = (patch) => onChange({ ...cfg, ...patch });
  const setDay = (d, patch) => set({ days: { ...cfg.days, [d]: { ...cfg.days[d], ...patch } } });
  const preview = (cfg.message || DEFAULT_MESSAGE).replace("{horarios}", scheduleText(cfg));

  return (
    <div className="border-t border-slate-800 pt-4 space-y-3" data-testid="human-hours">
      <div className="flex items-center justify-between gap-4">
        <div className="text-xs text-slate-500 leading-relaxed">
          <div className="text-slate-300 text-sm mb-1">Horário de atendimento humano</div>
          Fora desse horário, quem pedir para falar com atendente recebe o aviso abaixo e a conversa não entra
          na fila (o app não toca alarme). Dentro do horário, tudo funciona como hoje.
        </div>
        <Switch
          data-testid="human-hours-switch"
          checked={!!cfg.enabled}
          onCheckedChange={(v) => set({ enabled: v })}
        />
      </div>

      {cfg.enabled && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {DAYS.map((name, d) => {
              const day = cfg.days[d] || { enabled: false, start: "08:00", end: "18:00" };
              return (
                <div key={d} className="flex items-center gap-2 rounded-md border border-slate-800 bg-slate-950/50 px-3 py-2"
                  data-testid={`human-hours-day-${d}`}>
                  <Switch checked={!!day.enabled} onCheckedChange={(v) => setDay(d, { enabled: v })} />
                  <span className={`w-16 text-sm ${day.enabled ? "text-slate-200" : "text-slate-500"}`}>{name}</span>
                  {day.enabled ? (
                    <>
                      <Input type="time" value={day.start} onChange={(e) => setDay(d, { start: e.target.value })}
                        className="h-8 w-[6.5rem] bg-slate-950 border-slate-800 text-white text-sm" />
                      <span className="text-xs text-slate-500">às</span>
                      <Input type="time" value={day.end} onChange={(e) => setDay(d, { end: e.target.value })}
                        className="h-8 w-[6.5rem] bg-slate-950 border-slate-800 text-white text-sm" />
                    </>
                  ) : (
                    <span className="text-xs text-slate-500">fechado</span>
                  )}
                </div>
              );
            })}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <div className="text-slate-400 text-xs uppercase tracking-[0.15em]">Mensagem fora do horário</div>
              <Textarea
                data-testid="human-hours-message"
                rows={7}
                value={cfg.message ?? DEFAULT_MESSAGE}
                onChange={(e) => set({ message: e.target.value })}
                className="bg-slate-950 border-slate-800 text-white text-sm"
              />
              <div className="text-[11px] text-slate-500">
                Use <span className="font-mono text-emerald-300">{"{horarios}"}</span> para inserir a lista de horários.
              </div>
            </div>
            <div className="space-y-1">
              <div className="text-slate-400 text-xs uppercase tracking-[0.15em]">Prévia</div>
              <div className="whitespace-pre-wrap rounded-md border border-slate-800 bg-emerald-950/30 p-3 text-sm text-slate-200">
                {preview}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
